/**
 * Route map for the driver Active Context.
 *
 * Renders real OpenStreetMap data via Leaflet: the endpoints are geocoded with
 * Nominatim, the driving line is fetched from the public OSRM demo server, and
 * the path is overlaid with the prototype's (demo) traffic coloring. All three
 * services are free and keyed off the demo route's city names — a stand-in for
 * the Google Maps integration discussed in the design review.
 *
 * Results are cached per route and degrade gracefully: no OSRM route → straight
 * line between endpoints; geocode failure → the basemap still renders.
 */

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useEffect, useMemo, useState } from "react";
import { CircleMarker, MapContainer, Marker, Polyline, TileLayer, Tooltip, useMap } from "react-leaflet";

import { accent, text, traffic, zinc } from "../colors";
import { hash } from "../hash";
import type { RouteModel, RouteSegment } from "../types";

type LatLng = [number, number];

const geocodeCache = new Map<string, LatLng | null>();
const routeCache = new Map<string, LatLng[]>();

async function geocode(query: string): Promise<LatLng | null> {
  if (geocodeCache.has(query)) return geocodeCache.get(query) ?? null;
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(query)}`,
      { headers: { Accept: "application/json" } },
    );
    const data = (await res.json()) as { lat: string; lon: string }[];
    const hit: LatLng | null = data[0] ? [Number(data[0].lat), Number(data[0].lon)] : null;
    geocodeCache.set(query, hit);
    return hit;
  } catch {
    geocodeCache.set(query, null);
    return null;
  }
}

/** Road-following geometry from OSRM ([lat,lng]); falls back to a straight line. */
async function fetchRoadRoute(from: LatLng, to: LatLng): Promise<LatLng[]> {
  const key = `${from.join(",")}->${to.join(",")}`;
  const cached = routeCache.get(key);
  if (cached) return cached;
  try {
    const res = await fetch(
      `https://router.project-osrm.org/route/v1/driving/${from[1]},${from[0]};${to[1]},${to[0]}?overview=full&geometries=geojson`,
    );
    const data = (await res.json()) as { routes?: { geometry: { coordinates: [number, number][] } }[] };
    const coords = data.routes?.[0]?.geometry.coordinates;
    const line: LatLng[] = coords?.length ? coords.map(([lon, lat]) => [lat, lon]) : [from, to];
    routeCache.set(key, line);
    return line;
  } catch {
    return [from, to];
  }
}

function segmentColor(level: RouteSegment["level"]): string {
  return level === "heavy" ? traffic.heavy : level === "moderate" ? traffic.moderate : traffic.clear;
}

/** Split the road geometry into traffic-colored chunks proportional to segment weights. */
function trafficChunks(positions: LatLng[], segments: RouteSegment[]): { color: string; positions: LatLng[] }[] {
  if (positions.length < 2 || segments.length === 0) return [];
  const total = segments.reduce((sum, s) => sum + s.weight, 0);
  const last = positions.length - 1;
  const chunks: { color: string; positions: LatLng[] }[] = [];
  let startFrac = 0;
  for (const seg of segments) {
    const endFrac = startFrac + seg.weight / total;
    const startIdx = Math.floor(startFrac * last);
    const endIdx = Math.ceil(endFrac * last);
    chunks.push({ color: segmentColor(seg.level), positions: positions.slice(startIdx, endIdx + 1) });
    startFrac = endFrac;
  }
  return chunks;
}

const destinationIcon = L.divIcon({
  className: "",
  iconSize: [26, 34],
  iconAnchor: [13, 33],
  html: `<svg width="26" height="34" viewBox="0 0 26 34" xmlns="http://www.w3.org/2000/svg">
    <path d="M13 33 C2 19 0 15 0 11 A11 11 0 0 1 22 11 C22 15 20 19 13 33 Z" transform="translate(2,0)" fill="#ef4444" stroke="#f8fafc" stroke-width="2"/>
    <circle cx="13" cy="12" r="4.5" fill="#f8fafc"/>
  </svg>`,
});

/** Point at a 0–1 fraction along the route geometry. */
function pointAt(positions: LatLng[], frac: number): LatLng | undefined {
  if (positions.length === 0) return undefined;
  const idx = Math.round(Math.max(0, Math.min(1, frac)) * (positions.length - 1));
  return positions[idx];
}

/** Fit the map to the route and nearby markers whenever they change. */
function FitToRoute({ points }: { points: LatLng[] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length >= 2) {
      map.fitBounds(L.latLngBounds(points), { padding: [28, 28] });
    } else if (points.length === 1) {
      map.setView(points[0], 9);
    }
  }, [map, points]);
  return null;
}

function LegendItem({ color, label, dot }: { color: string; label: string; dot?: boolean }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Box sx={{ width: dot ? 9 : 18, height: dot ? 9 : 4, borderRadius: dot ? "50%" : 2, bgcolor: color }} />
      <Typography sx={{ fontSize: 12, color: text.secondary }}>{label}</Typography>
    </Box>
  );
}

export function RouteMap({ route }: { route: RouteModel }) {
  const [positions, setPositions] = useState<LatLng[]>([]);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");

  useEffect(() => {
    let active = true;
    setStatus("loading");
    setPositions([]);

    (async () => {
      const [from, to] = await Promise.all([geocode(route.origin), geocode(route.destination)]);
      if (!active) return;
      if (!from && !to) {
        setStatus("error");
        return;
      }
      if (from && to) {
        const line = await fetchRoadRoute(from, to);
        if (!active) return;
        setPositions(line);
      } else {
        setPositions([(from ?? to) as LatLng]);
      }
      setStatus("ready");
    })();

    return () => {
      active = false;
    };
  }, [route.origin, route.destination]);

  const chunks = useMemo(() => trafficChunks(positions, route.segments), [positions, route.segments]);
  const origin = positions[0];
  const destination = positions.length >= 2 ? positions[positions.length - 1] : undefined;

  // Intermediate stops along the route, placed at their fractional position.
  const stopMarkers = useMemo<{ pos: LatLng; label: string }[]>(() => {
    if (positions.length < 2) return [];
    return route.cities
      .filter((c) => c.at > 0.02 && c.at < 0.98)
      .map((c) => ({ pos: pointAt(positions, c.at), label: c.label }))
      .filter((m): m is { pos: LatLng; label: string } => m.pos !== undefined);
  }, [positions, route.cities]);

  // A few teammate trucks scattered near this driver's lane (demo).
  const teamTrucks = useMemo<{ pos: LatLng; truck: string }[]>(() => {
    if (positions.length < 2) return [];
    const seed = `${route.origin}->${route.destination}`;
    const trucks: { pos: LatLng; truck: string }[] = [];
    for (let i = 0; i < 4; i++) {
      const anchor = pointAt(positions, (i + 1) / 5);
      if (!anchor) continue;
      const dLat = ((hash(`ttlat::${seed}::${i}`) % 1000) / 1000 - 0.5) * 0.7;
      const dLng = ((hash(`ttlng::${seed}::${i}`) % 1000) / 1000 - 0.5) * 0.7;
      trucks.push({
        pos: [anchor[0] + dLat, anchor[1] + dLng],
        truck: String(1000 + (hash(`tt::${seed}::${i}`) % 9000)),
      });
    }
    return trucks;
  }, [positions, route.origin, route.destination]);

  const fitPoints = useMemo(() => [...positions, ...teamTrucks.map((t) => t.pos)], [positions, teamTrucks]);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: text.primary }}>
          {route.origin} → {route.destination}
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <LegendItem color={traffic.clear} label="Clear" />
          <LegendItem color={traffic.moderate} label="Moderate" />
          <LegendItem color={traffic.heavy} label="Heavy" />
          <LegendItem dot color="#e2e8f0" label="Stops" />
          <LegendItem dot color={accent.purple} label="Team" />
        </Box>
      </Box>

      <Box
        sx={{
          position: "relative",
          height: 280,
          borderRadius: "10px",
          overflow: "hidden",
          border: `1px solid ${zinc[800]}`,
          // Leaflet attribution/controls tuned for the dark surface.
          "& .leaflet-container": { bgcolor: zinc[950], fontFamily: "inherit" },
          // Lift the very dark basemap so roads and labels read clearly.
          "& .leaflet-tile": { filter: "brightness(1.6) contrast(1.05)" },
          "& .leaflet-control-attribution": { bgcolor: "rgba(0,0,0,0.55)", color: zinc[400] },
          "& .leaflet-control-attribution a": { color: zinc[300] },
        }}
      >
        <MapContainer
          center={[39.5, -98.35]}
          zoom={4}
          scrollWheelZoom={false}
          style={{ height: "100%", width: "100%" }}
          attributionControl
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          />

          {/* white casing under the colored traffic segments */}
          {positions.length >= 2 && (
            <Polyline positions={positions} pathOptions={{ color: "#f8fafc", weight: 8, opacity: 0.9 }} />
          )}
          {chunks.map((chunk, i) => (
            <Polyline key={i} positions={chunk.positions} pathOptions={{ color: chunk.color, weight: 5, opacity: 1 }} />
          ))}

          {/* teammate trucks near this lane */}
          {teamTrucks.map((t) => (
            <CircleMarker
              key={`team-${t.truck}`}
              center={t.pos}
              radius={6}
              pathOptions={{ color: "#0d0d0f", weight: 2, fillColor: accent.purple, fillOpacity: 0.95 }}
            >
              <Tooltip>{`Truck ${t.truck} · team`}</Tooltip>
            </CircleMarker>
          ))}

          {/* intermediate stops along the route */}
          {stopMarkers.map((s) => (
            <CircleMarker
              key={`stop-${s.label}`}
              center={s.pos}
              radius={5}
              pathOptions={{ color: "#0d0d0f", weight: 2, fillColor: "#e2e8f0", fillOpacity: 1 }}
            >
              <Tooltip>{s.label}</Tooltip>
            </CircleMarker>
          ))}

          {origin && (
            <CircleMarker
              center={origin}
              radius={7}
              pathOptions={{ color: "#f8fafc", weight: 3, fillColor: "#3b82f6", fillOpacity: 1 }}
            >
              <Tooltip>{route.origin}</Tooltip>
            </CircleMarker>
          )}
          {destination && <Marker position={destination} icon={destinationIcon} />}

          <FitToRoute points={fitPoints} />
        </MapContainer>

        {status !== "ready" && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(13,13,15,0.55)",
              pointerEvents: "none",
              zIndex: 500,
            }}
          >
            <Typography sx={{ fontSize: 13, color: text.secondary }}>
              {status === "loading" ? "Loading map…" : "Map data unavailable"}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
